<script setup lang="ts">
import { computed } from "vue";
import type { ButtonSize, ButtonTone } from "@/components/shared/buttons/types";

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    size?: ButtonSize;
    tone?: ButtonTone;
    prependIcon?: string;
    /** Renders a square, label-less button showing only `prependIcon`. */
    iconOnly?: boolean;
  }>(),
  {
    loading: false,
    disabled: false,
    type: "button",
    size: "large",
    tone: "brand",
    prependIcon: undefined,
    iconOnly: false,
  }
);

const emit = defineEmits<{ click: [event: MouseEvent] }>();

const toneClass = computed(() => [
  props.tone === "danger" ? "primary-button--danger" : "",
  props.tone === "dark" ? "primary-button--dark" : "",
  props.iconOnly ? "primary-button--icon-only" : "",
]);
</script>

<template>
  <v-btn
    v-if="props.iconOnly"
    key="icon-only"
    :type="props.type"
    :loading="props.loading"
    :disabled="props.disabled"
    :size="props.size"
    :icon="props.prependIcon"
    density="comfortable"
    variant="flat"
    class="orbis-btn primary-button tw:inline-flex tw:items-center tw:justify-center"
    :class="toneClass"
    @click="emit('click', $event)"
  />
  <v-btn
    v-else
    key="full"
    :type="props.type"
    :loading="props.loading"
    :disabled="props.disabled"
    :size="props.size"
    :prepend-icon="props.prependIcon"
    variant="flat"
    class="orbis-btn primary-button tw:inline-flex tw:items-center tw:gap-2 tw:text-sm tw:tracking-[-0.006em]"
    :class="toneClass"
    @click="emit('click', $event)"
  >
    <slot />
  </v-btn>
</template>

<style scoped>
/* Layout, gap and type scale live as `tw:` utilities on the element; what stays
   here is what Tailwind cannot express against Vuetify — the token-driven
   gradient/palette, and the `!important` needed to outrank `.v-btn`'s own
   padding and radius. Motion is shared via `.orbis-btn` in style.css.
   Design spec: 44px tall, 0 26px padding, 11px radius, weight 640. */
.primary-button {
  --btn-press-scale: 0.98;

  /* Fallback, not a declaration: an ancestor may set `--btn-px` to tune the
     gutter, and a real declaration here would win. */
  padding: 0 var(--btn-px, 26px) !important;

  border: 0;
  border-radius: var(--btn-radius) !important;

  background: linear-gradient(180deg, var(--btn-brand-top), var(--btn-brand-bottom)) !important;
  color: var(--btn-on-brand) !important;

  font-size: var(--btn-fs);
  font-weight: 640;

  box-shadow: var(--btn-shadow-brand);
}

.primary-button.primary-button--icon-only {
  min-width: 0 !important;
  padding: 0 !important;
}

.primary-button:hover:not(:disabled) {
  box-shadow: var(--btn-shadow-brand-hover);
}

.primary-button--danger {
  background: linear-gradient(180deg, var(--btn-danger-top), var(--btn-danger-bottom)) !important;
  color: var(--btn-on-danger) !important;

  box-shadow: var(--btn-shadow-danger);
}

.primary-button--danger:hover:not(:disabled) {
  box-shadow: var(--btn-shadow-danger-hover);
}

.primary-button--dark {
  background: linear-gradient(180deg, var(--btn-dark-top), var(--btn-dark-bottom)) !important;
  color: var(--btn-on-dark) !important;

  box-shadow: var(--btn-shadow-dark);
}

.primary-button--dark:hover:not(:disabled) {
  box-shadow: var(--btn-shadow-dark-hover);
}
</style>
